import { useCallback, useState } from 'react';
import { GameState, apiService, Situation } from '../services/api';

interface UseGameActionsProps {
  gameCode: string;
  playerId: string;
  onStateUpdate: (newState: GameState) => void;
}

export function useGameActions({ gameCode, playerId, onStateUpdate }: UseGameActionsProps) {
  const [isLoading, setIsLoading] = useState(false);

  const advanceGame = useCallback(async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const response = await apiService.advanceGame(gameCode, playerId);
      if (response.success) {
        onStateUpdate(response.gameState);
      }
    } catch (err) {
      console.error('Failed to advance game:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [gameCode, playerId, onStateUpdate, isLoading]);


  const submitGuesses = useCallback(async (guesses: { [playerId: string]: number }) => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const response = await apiService.submitGuesses(gameCode, playerId, guesses);
      if (response.success) {
        onStateUpdate(response.gameState);
      }
      return response;
    } catch (err) {
      console.error('Failed to submit guesses:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [gameCode, playerId, onStateUpdate, isLoading]);

  const getRandomSituations = useCallback(async (): Promise<Situation[]> => {
    if (isLoading) return [];
    setIsLoading(true);
    try {
      return await apiService.getRandomSituations(gameCode, playerId);
    } catch (err) {
      console.error('Failed to get random topics:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [gameCode, playerId, isLoading]);

  const setGameSituation = useCallback(async (situationId: string) => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const response = await apiService.setGameSituation(gameCode, playerId, situationId);
      if (response.success) {
        onStateUpdate(response.gameState);
      }
    } catch (err) {
      console.error('Failed to set game topic:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [gameCode, playerId, onStateUpdate, isLoading]);

  return {
    advanceGame,
    submitGuesses,
    getRandomSituations,
    setGameSituation,
    isLoading
  };
}